import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Stack} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {createContext, type PropsWithChildren, use, useCallback, useMemo, useState} from 'react'

import styles from './styles.module.css'

type PayloadType = 'nav_menu' | 'user_menu' | 'create_menu' | 'agents_menu' | 'account_switch_dialog'

export type {PayloadType}

/**
 * Context values exposed via `useTopNavContext`:
 * - `hasLocalNavigation`: whether the navigation has local navigation tabs below the main bar.
 * - `searchOpen`: whether the search input is currently open.
 * - `isSearchPage`: whether the current page is the search page.
 * - `setSearchOpen(open)`: toggles the search input open state.
 * - `topNavHidden`: whether the top navigation is currently hidden.
 * - `setTopNavHidden(hidden)`: sets the hidden state of the top navigation.
 * - `shouldLoadCopilotChat`: whether Copilot Chat assets should be loaded.
 * - `loadCopilotChat()`: triggers loading of Copilot Chat assets.
 */
const TopNavContext = createContext<{
  hasLocalNavigation: boolean
  searchOpen: boolean
  isSearchPage: boolean
  setSearchOpen: (open: boolean) => void
  topNavHidden: boolean
  setTopNavHidden: (hidden: boolean) => void
  shouldLoadCopilotChat: boolean
  loadCopilotChat: () => void
  getPayloadsUrl: (type: PayloadType) => string
  payloadsBaseUrl: string
}>({
  hasLocalNavigation: false,
  searchOpen: false,
  isSearchPage: false,
  setSearchOpen: () => {},
  setTopNavHidden: () => {},
  topNavHidden: false,
  shouldLoadCopilotChat: false,
  loadCopilotChat: () => {},
  getPayloadsUrl: (type: PayloadType) => `/_global-navigation/payloads.json?type=${type}`,
  payloadsBaseUrl: '/_global-navigation/payloads.json',
})

export type TopNavProps = React.PropsWithChildren<{
  /** Whether the navigation has local navigation tabs below the main bar */
  hasLocalNavigation: boolean
  /** Whether the search input is on the search page */
  isSearchPage: boolean
  /** Whether the search input is a "jump to" search */
  isJumpToSearch: boolean
  /** URL for fetching payload data */
  payloadsUrl?: string
}>

export function shouldHideTopNav(urlParams: URLSearchParams, isSearchPage: boolean, isJumpToSearch: boolean) {
  const shouldHide = isSearchPage && !isJumpToSearch && !urlParams.get('q')?.trim()

  return shouldHide
}

export function TopNavProvider({
  children,
  hasLocalNavigation,
  isSearchPage,
  isJumpToSearch,
  payloadsUrl = '/_global-navigation/payloads.json',
}: TopNavProps) {
  // Defaulting to false so we don't load chat on every page.
  const [shouldLoadCopilotChat, setShouldLoadCopilotChat] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [topNavHidden, setTopNavHidden] = useState(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)
    // This can be overridden later via the context setter,
    // but set the default here based on URL params so we don't flash the nav on initial load
    return shouldHideTopNav(urlParams, isSearchPage, isJumpToSearch)
  })

  const loadCopilotChat = useCallback(() => {
    setShouldLoadCopilotChat(true)
  }, [])

  const getPayloadsUrl = useCallback(
    (type: PayloadType) => {
      const url = new URL(payloadsUrl, ssrSafeLocation.origin)
      url.searchParams.set('type', type)
      return url.toString()
    },
    [payloadsUrl],
  )

  const context = useMemo(
    () => ({
      hasLocalNavigation,
      searchOpen,
      isSearchPage,
      setSearchOpen,
      setTopNavHidden,
      topNavHidden,
      shouldLoadCopilotChat,
      loadCopilotChat,
      getPayloadsUrl,
      payloadsBaseUrl: payloadsUrl,
    }),
    [
      hasLocalNavigation,
      isSearchPage,
      loadCopilotChat,
      searchOpen,
      shouldLoadCopilotChat,
      topNavHidden,
      getPayloadsUrl,
      payloadsUrl,
    ],
  )

  return <TopNavContext value={context}>{children}</TopNavContext>
}

/**
 * React hook to access the TopNav context.
 *
 * Returns an object with the following values:
 * - `hasLocalNavigation` (boolean): Whether local navigation tabs are present below the main bar.
 * - `searchOpen` (boolean): Whether the search input is currently open.
 * - `isSearchPage` (boolean): Whether the search input is on the search page.
 * - `setSearchOpen` (function): Function to set the `searchOpen` state.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useTopNavContext() {
  return use(TopNavContext)
}

function TopNavComponent({
  children,
  className,
}: PropsWithChildren<{
  className?: string
}>) {
  const {topNavHidden} = useTopNavContext()

  const [{left, center, right, above}, rest] = useSlots(children, {
    left: Left,
    center: Center,
    right: Right,
    above: Above,
  })

  return (
    <Stack
      as="header"
      className={clsx(
        'GlobalNav',
        {
          'd-none': topNavHidden,
        },
        styles.appHeader,
        className,
      )}
      role="banner"
      aria-label="Global Navigation Menu"
      gap="none"
    >
      {above}
      <Stack direction="horizontal" align="center" justify="center">
        {left}
        {center}
        {right}
      </Stack>
      {rest}
    </Stack>
  )
}

function Left({children}: React.PropsWithChildren) {
  const {hasLocalNavigation} = use(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-left"
      direction="horizontal"
      className={clsx(styles.left, hasLocalNavigation && styles.withLocalNavigation)}
      gap="condensed"
      padding="normal"
    >
      {children}
    </Stack>
  )
}

function Center({children, className}: React.PropsWithChildren<{className?: string}>) {
  const {hasLocalNavigation} = use(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-center"
      direction="horizontal"
      gap="condensed"
      padding="normal"
      className={clsx(styles.center, hasLocalNavigation && styles.withLocalNavigation, className)}
    >
      {children}
    </Stack>
  )
}

function Right({children}: React.PropsWithChildren) {
  const {hasLocalNavigation} = use(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-right"
      direction="horizontal"
      align="center"
      gap="condensed"
      padding="normal"
      className={clsx(styles.right, hasLocalNavigation && styles.withLocalNavigation)}
    >
      {children}
    </Stack>
  )
}

function Above({children}: React.PropsWithChildren) {
  return <>{children}</>
}

/**
 * The TopNav component lays the structure for the top navigation bar within product.
 * Use the `TopNav.Above`, `TopNav.Left`, `TopNav.Center`, and `TopNav.Right` slots to define the content above, left, center, and right sides of the navigation bar.
 * - `TopNav.Above`: Content rendered above the main navigation bar (e.g., banners, alerts).
 * - `TopNav.Left`, `TopNav.Center`, `TopNav.Right`: Content rendered on the left, center, and right of the navigation bar, respectively.
 * Any additional children will be shown below the initial bar (such as Local Navigation).
 */
export const TopNav = Object.assign(TopNavComponent, {Above, Left, Center, Right})
